import React from "react";
import { useDrop, useDrag } from "react-dnd";
import FormControlTextArea from "../../components/Common/FormControlTextArea";
import { Form } from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";
import useCustomTranslation from "../../hooks/useCustomTranslation";

const ExerciseItem = ({
  ex,
  indexOrder,
  session,
  isform,
  updateExerciseField,
  moveExercise,
  setSessions,
  setForm,
}) => {
  const { trans } = useCustomTranslation();
  const ref = React.useRef(null);

  const [{ isDragging }, drag] = useDrag({
    type: "EXERCISE_ITEM",
    item: {
      indexOrder,
      sessionId: isform ? session.clientWorkoutPlanSessionId : session.id,
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: "EXERCISE_ITEM",
    hover: (item, monitor) => {
      if (!ref.current) return;
      const dragIndex = item.indexOrder;
      const hoverIndex = indexOrder;
      const dragSessionId = item.sessionId;
      const hoverSessionId = isform
        ? session.clientWorkoutPlanSessionId
        : session.id;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex && dragSessionId === hoverSessionId) return;

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      // Determine mouse position
      const clientOffset = monitor.getClientOffset();
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%
      if (
        (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) ||
        (dragIndex > hoverIndex && hoverClientY > hoverMiddleY)
      ) {
        return;
      }

      // Time to actually perform the action
      if (dragSessionId === hoverSessionId) {
        moveExercise(dragIndex, hoverIndex);
      }
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.indexOrder = hoverIndex;
    },
  });

  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={`exercise-card ${isDragging ? "dragging" : ""}`}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      {ex.blobUrl ? (
        <img src={ex.blobUrl} alt={ex.name} className="exercise-image" />
      ) : (
        <div className="exercise-placeholder">
          {trans("ClientMealPlan.noImage")}
        </div>
      )}
      <div className="exercise-details ml-2">
        <span className="exercise-name">
          {!isform
            ? ex.name?.charAt(0).toUpperCase() + ex.name?.slice(1)
            : ex.exerciseName?.charAt(0).toUpperCase() +
              ex.exerciseName?.slice(1)}
        </span>
        <div className="sets-reps">
          <div className="input-wrapper-session">
            <span className="input-label-session">
              {trans("ClientWorkout.sets")}
            </span>
            <input
              type="number"
              min="1"
              value={ex.sets}
              onChange={(e) =>
                updateExerciseField(ex.exerciseId, "sets", e.target.value)
              }
            />
          </div>

          <div className="input-wrapper-session">
            <span className="input-label-session">
              {trans("ClientWorkout.reps")}
            </span>
            <input
              type="number"
              min="1"
              max="100"
              value={ex.reps}
              onChange={(e) =>
                updateExerciseField(ex.exerciseId, "reps", e.target.value)
              }
            />
          </div>

          {isform && (
            <div className="input-wrapper-session">
              <span className="input-label-session">
                {trans("ClientWorkout.secs")}
              </span>
              <input
                type="number"
                min="1"
                max="100"
                value={ex.sec}
                onChange={(e) =>
                  updateExerciseField(ex.exerciseId, "sec", e.target.value)
                }
              />
            </div>
          )}
        </div>
        <div className="exercise-note">
          <Form.Group controlId={`note-${ex.exerciseId}`}>
            <FormControlTextArea
              placeholder="Add a note..."
              name="exerciseNote"
              value={ex.notes || ex.exerciseNote}
              onChange={(e) =>
                updateExerciseField(
                  ex.exerciseId,
                  "exerciseNote",
                  e.target.value
                )
              }
              rows={2}
            />
          </Form.Group>
        </div>
      </div>

      <button
        className="remove-exercise"
        onClick={() => {
          if (isform) {
            setForm((prevData) => ({
              ...prevData,
              clientWorkoutPlanSessionRequests:
                prevData.clientWorkoutPlanSessionRequests?.map((s) =>
                  s.clientWorkoutPlanSessionId ===
                  session.clientWorkoutPlanSessionId
                    ? {
                        ...s,
                        clientWorkoutPlanExerciseRequests:
                          s.clientWorkoutPlanExerciseRequests.filter(
                            (e) => e.exerciseId !== ex.exerciseId
                          ),
                      }
                    : s
                ),
            }));
          } else {
            setSessions((prevSessions) =>
              prevSessions?.map((s) =>
                s.id === session.id
                  ? {
                      ...s,
                      exercises: s.exercises.filter(
                        (e) => e.exerciseId !== ex.exerciseId
                      ),
                    }
                  : s
              )
            );
          }
        }}
      >
        X
      </button>
    </div>
  );
};

const Session = ({ session, setSessions, form, setForm, isform }) => {
  const { trans } = useCustomTranslation();
  const [{ isOver }, drop] = useDrop(() => ({
    accept: "EXERCISE",
    drop: (item) => {
      addExerciseToSession(item.exercise);
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  }));

  const addExerciseToSession = (exercise) => {
    if (!isform) {
      setSessions((prevSessions) =>
        prevSessions?.map((s) => {
          if (s.id === session.id) {
            if (
              s.exercises?.some((ex) => ex.exerciseId === exercise.exerciseId)
            ) {
              return s;
            }
            return {
              ...s,
              exercises: [
                ...(s.exercises || []),
                {
                  ...exercise,
                  sets: exercise.sets || "1",
                  reps: exercise.reps || "1",
                  exerciseNote: exercise.exerciseNote || "",
                  indexOrder: s.exercises?.length || 0,
                },
              ],
            };
          }
          return s;
        })
      );
    } else {
      setForm((prevForm) => ({
        ...prevForm,
        clientWorkoutPlanSessionRequests:
          prevForm?.clientWorkoutPlanSessionRequests?.map((s) => {
            if (
              s.clientWorkoutPlanSessionId ===
              session.clientWorkoutPlanSessionId
            ) {
              if (
                s?.clientWorkoutPlanExerciseRequests?.some(
                  (ex) => ex.exerciseId === exercise.exerciseId
                )
              ) {
                return s;
              }
              return {
                ...s,
                clientWorkoutPlanExerciseRequests: [
                  ...(s.clientWorkoutPlanExerciseRequests || []),
                  {
                    clientWorkoutPlanSessionId:
                      session.clientWorkoutPlanSessionId,
                    clientWorkoutPlanExerciseId: uuidv4(),
                    exerciseName: exercise.name,
                    blobUrl: exercise.blobUrl || "",
                    exerciseId: exercise.exerciseId,
                    sets: "1",
                    reps: "1",
                    sec: "",
                    setType: "",
                    dropSet: "",
                    notes: exercise.note || "",
                    indexOrder:
                      s.clientWorkoutPlanExerciseRequests?.length || 0,
                  },
                ],
              };
            }
            return s;
          }),
      }));
    }
  };

  const moveExercise = (dragIndex, hoverIndex) => {
    if (isform) {
      setForm((prevForm) => {
        const sessionIndex =
          prevForm.clientWorkoutPlanSessionRequests.findIndex(
            (s) =>
              s.clientWorkoutPlanSessionId ===
              session.clientWorkoutPlanSessionId
          );
        if (sessionIndex === -1) return prevForm;

        const updatedSessions = [...prevForm.clientWorkoutPlanSessionRequests];
        const exercises = [
          ...updatedSessions[sessionIndex].clientWorkoutPlanExerciseRequests,
        ];
        // Remove the dragged item
        const [removed] = exercises.splice(dragIndex, 1);
        // Insert it at the new position
        exercises.splice(hoverIndex, 0, removed);

        // Update the indexes to match their new positions
        const updatedExercises = exercises.map((exercise, idx) => ({
          ...exercise,
          indexOrder: idx,
        }));

        updatedSessions[sessionIndex] = {
          ...updatedSessions[sessionIndex],
          clientWorkoutPlanExerciseRequests: updatedExercises,
        };

        return {
          ...prevForm,
          clientWorkoutPlanSessionRequests: updatedSessions,
        };
      });
    } else {
      setSessions((prevSessions) => {
        const sessionIndex = prevSessions.findIndex((s) => s.id === session.id);
        if (sessionIndex === -1) return prevSessions;

        const updatedSessions = [...prevSessions];
        const exercises = [...updatedSessions[sessionIndex].exercises];
        // Remove the dragged item
        const [removed] = exercises.splice(dragIndex, 1);
        // Insert it at the new position
        exercises.splice(hoverIndex, 0, removed);

        // Update the indexes to match their new positions
        const updatedExercises = exercises.map((exercise, idx) => ({
          ...exercise,
          indexOrder: idx,
        }));

        updatedSessions[sessionIndex] = {
          ...updatedSessions[sessionIndex],
          exercises: updatedExercises,
        };

        return updatedSessions;
      });
    }
  };

  const updateExerciseField = (exerciseId, field, value) => {
    if (!isform) {
      setSessions((prevSessions) =>
        prevSessions?.map((s) =>
          s.id === session.id
            ? {
                ...s,
                exercises: s?.exercises?.map((exercise) =>
                  exercise.exerciseId === exerciseId
                    ? { ...exercise, [field]: value }
                    : exercise
                ),
              }
            : s
        )
      );
    } else {
      setForm((prevData) => {
        const updatedData = { ...prevData };
        updatedData.clientWorkoutPlanSessionRequests =
          updatedData?.clientWorkoutPlanSessionRequests?.map((plan) => ({
            ...plan,
            clientWorkoutPlanExerciseRequests:
              plan.clientWorkoutPlanExerciseRequests?.map((detail) =>
                detail.exerciseId === exerciseId
                  ? {
                      ...detail,
                      [field === "exerciseNote" ? "notes" : field]: value,
                    }
                  : detail
              ),
          }));
        return updatedData;
      });
    }
  };

  const exercises = isform
    ? [...(session.clientWorkoutPlanExerciseRequests || [])].sort(
        (a, b) => (a.indexOrder || 0) - (b.indexOrder || 0)
      )
    : [...(session.exercises || [])].sort(
        (a, b) => (a.indexOrder || 0) - (b.indexOrder || 0)
      );

  return (
    <div ref={drop} className={`session ${isOver ? "highlight" : ""}  h-100`}>
      <h6>{!isform ? session.name : session.sessionName}</h6>
      {exercises.length === 0 ? (
        <p className="empty-text">{trans("ClientMealPlan.dragExerHere")}</p>
      ) : (
        exercises.map((ex, index) => (
          <ExerciseItem
            key={ex.exerciseId || ex.clientWorkoutPlanExerciseId || index}
            ex={ex}
            indexOrder={ex.indexOrder || index}
            session={session}
            isform={isform}
            updateExerciseField={updateExerciseField}
            moveExercise={moveExercise}
            setSessions={setSessions}
            setForm={setForm}
          />
        ))
      )}
    </div>
  );
};

export default Session;
